import { randomUUID } from 'node:crypto';
import { Router } from 'express';
import { z } from 'zod';
import { requireAdmin, requireAuth, type AuthenticatedRequest } from './auth';
import { pool } from './db';
import { decryptIdentityDocument, encryptIdentityDocument } from './privacy';

export const identityVerificationRouter = Router();

const dataUrlSchema = z.string().max(2_100_000).refine((value) => {
  const match = /^data:image\/(jpeg|png|webp);base64,([A-Za-z0-9+/]+={0,2})$/.exec(value);
  if (!match) return false;
  try {
    const bytes = Buffer.from(match[2], 'base64');
    const signatureMatches = match[1] === 'jpeg'
      ? bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff
      : match[1] === 'png'
        ? bytes.subarray(0, 8).equals(Buffer.from([0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]))
        : bytes.subarray(0, 4).toString('ascii') === 'RIFF'
          && bytes.subarray(8, 12).toString('ascii') === 'WEBP';
    return signatureMatches && bytes.length >= 1_000 && bytes.length <= 1_500_000;
  } catch { return false; }
}, 'Invalid identity document image');

const submissionSchema = z.object({
  frontDataUrl: dataUrlSchema,
  backDataUrl: dataUrlSchema,
});

const reviewSchema = z.object({
  decision: z.enum(['verified', 'rejected']),
  note: z.string().trim().max(1000).default(''),
}).refine((value) => value.decision !== 'rejected' || value.note.length >= 5, {
  message: 'A rejection reason is required',
});

identityVerificationRouter.get('/v1/me/identity-verification', requireAuth,
  async (request: AuthenticatedRequest, response) => {
    if (request.authUser!.accountType !== 'candidate') {
      response.status(403).json({ error: 'Candidate account required' }); return;
    }
    const result = await pool.query(
      `SELECT p.identity_status AS "identityStatus",
              s.status AS "submissionStatus",s.submitted_at AS "submittedAt",
              s.reviewed_at AS "reviewedAt",s.admin_note AS "adminNote",
              (s.front_image_encrypted IS NOT NULL AND s.back_image_encrypted IS NOT NULL) AS "hasDocuments"
       FROM profiles p
       LEFT JOIN identity_verification_submissions s ON s.user_id=p.user_id
       WHERE p.user_id=$1`,
      [request.authUser!.id],
    );
    if (!result.rows[0]) { response.status(404).json({ error: 'Profile not found' }); return; }
    response.setHeader('Cache-Control', 'no-store');
    response.json(result.rows[0]);
  });

identityVerificationRouter.put('/v1/me/identity-verification', requireAuth,
  async (request: AuthenticatedRequest, response) => {
    if (request.authUser!.accountType !== 'candidate') {
      response.status(403).json({ error: 'Candidate account required' }); return;
    }
    const parsed = submissionSchema.safeParse(request.body);
    if (!parsed.success) { response.status(400).json({ error: 'Invalid identity document images' }); return; }
    const client = await pool.connect();
    try {
      await client.query('BEGIN');
      const profile = await client.query<{ identity_status: string }>(
        'SELECT identity_status FROM profiles WHERE user_id=$1 FOR UPDATE',
        [request.authUser!.id],
      );
      if (!profile.rows[0]) {
        await client.query('ROLLBACK'); response.status(404).json({ error: 'Profile not found' }); return;
      }
      if (profile.rows[0].identity_status === 'verified') {
        await client.query('ROLLBACK'); response.status(409).json({ error: 'Identity is already verified' }); return;
      }
      const result = await client.query(
        `INSERT INTO identity_verification_submissions
           (id,user_id,front_image_encrypted,back_image_encrypted,status,admin_note,submitted_at,reviewed_at,reviewed_by,updated_at)
         VALUES ($1,$2,$3,$4,'reviewing','',NOW(),NULL,NULL,NOW())
         ON CONFLICT (user_id) DO UPDATE SET
           front_image_encrypted=EXCLUDED.front_image_encrypted,
           back_image_encrypted=EXCLUDED.back_image_encrypted,status='reviewing',admin_note='',
           submitted_at=NOW(),reviewed_at=NULL,reviewed_by=NULL,updated_at=NOW()
         RETURNING status AS "submissionStatus",submitted_at AS "submittedAt"`,
        [randomUUID(), request.authUser!.id,
          encryptIdentityDocument(parsed.data.frontDataUrl),
          encryptIdentityDocument(parsed.data.backDataUrl)],
      );
      await client.query(
        "UPDATE profiles SET identity_status='reviewing',identity_note='',updated_at=NOW() WHERE user_id=$1",
        [request.authUser!.id],
      );
      await client.query(
        "INSERT INTO audit_events (user_id,event_type,details) VALUES ($1,'identity_documents_submitted','{}'::jsonb)",
        [request.authUser!.id],
      );
      await client.query('COMMIT');
      response.setHeader('Cache-Control', 'no-store');
      response.json({ identityStatus: 'reviewing', ...result.rows[0], reviewedAt: null,
        adminNote: '', hasDocuments: true });
    } catch (error) { await client.query('ROLLBACK'); throw error; }
    finally { client.release(); }
  });

identityVerificationRouter.get('/admin/identity-verifications', requireAuth, requireAdmin,
  async (_request, response) => {
    const result = await pool.query(
      `SELECT s.id,s.user_id AS "userId",u.display_name AS "displayName",u.email,u.username,
              s.status,s.admin_note AS "adminNote",s.submitted_at AS "submittedAt",
              s.reviewed_at AS "reviewedAt",
              (s.front_image_encrypted IS NOT NULL AND s.back_image_encrypted IS NOT NULL) AS "hasDocuments"
       FROM identity_verification_submissions s JOIN users u ON u.id=s.user_id
       ORDER BY CASE WHEN s.status='reviewing' THEN 0 ELSE 1 END,s.submitted_at DESC`,
    );
    response.setHeader('Cache-Control', 'no-store');
    response.json({ submissions: result.rows });
  });

identityVerificationRouter.get('/admin/identity-verifications/:id/documents', requireAuth, requireAdmin,
  async (request, response) => {
    const id = z.uuid().safeParse(request.params.id);
    if (!id.success) { response.status(400).json({ error: 'Invalid submission ID' }); return; }
    const result = await pool.query<{ front_image_encrypted: string | null; back_image_encrypted: string | null }>(
      `SELECT front_image_encrypted,back_image_encrypted
       FROM identity_verification_submissions WHERE id=$1`, [id.data],
    );
    const document = result.rows[0];
    if (!document?.front_image_encrypted || !document.back_image_encrypted) {
      response.status(404).json({ error: 'Identity documents are no longer available' }); return;
    }
    response.setHeader('Cache-Control', 'no-store, no-cache, must-revalidate');
    response.setHeader('Pragma', 'no-cache');
    response.json({
      frontDataUrl: decryptIdentityDocument(document.front_image_encrypted),
      backDataUrl: decryptIdentityDocument(document.back_image_encrypted),
    });
  });

identityVerificationRouter.put('/admin/identity-verifications/:id/review', requireAuth, requireAdmin,
  async (request: AuthenticatedRequest, response) => {
    const id = z.uuid().safeParse(request.params.id);
    const parsed = reviewSchema.safeParse(request.body);
    if (!id.success || !parsed.success) { response.status(400).json({ error: 'Invalid review data' }); return; }
    const client = await pool.connect();
    try {
      await client.query('BEGIN');
      const current = await client.query<{ user_id: string; status: string }>(
        'SELECT user_id,status FROM identity_verification_submissions WHERE id=$1 FOR UPDATE', [id.data],
      );
      if (!current.rows[0]) {
        await client.query('ROLLBACK'); response.status(404).json({ error: 'Submission not found' }); return;
      }
      if (current.rows[0].status !== 'reviewing') {
        await client.query('ROLLBACK'); response.status(409).json({ error: 'Submission was already reviewed' }); return;
      }
      await client.query(
        `UPDATE identity_verification_submissions SET status=$1,admin_note=$2,
           front_image_encrypted=NULL,back_image_encrypted=NULL,
           reviewed_at=NOW(),reviewed_by=$3,updated_at=NOW() WHERE id=$4`,
        [parsed.data.decision, parsed.data.note, request.authUser!.id, id.data],
      );
      await client.query(
        `UPDATE profiles SET identity_status=$1,identity_note=$2,updated_at=NOW() WHERE user_id=$3`,
        [parsed.data.decision === 'verified' ? 'verified' : 'unverified',
          parsed.data.note, current.rows[0].user_id],
      );
      await client.query(
        "INSERT INTO audit_events (user_id,event_type,details) VALUES ($1,'identity_verification_reviewed',$2::jsonb)",
        [request.authUser!.id, JSON.stringify({ targetUserId: current.rows[0].user_id,
          decision: parsed.data.decision })],
      );
      await client.query('COMMIT');
      response.json({ reviewed: true });
    } catch (error) { await client.query('ROLLBACK'); throw error; }
    finally { client.release(); }
  });
